import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { DateTime as d } from "luxon";
import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { SimpleDateTimePicker } from "../../../SimpleDateTimePicker/intex";
import { CONSUMER_REQUEST_BOOKING_EXTENSION } from "../../../../graphql/bookings/consumerRequestBookingExtensionMutation";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage } from "../../../utils";
import { IBooking } from "../../../../reducers/bookings/types";
import { IAppState } from "../../../../store";

interface IDateTime {
  date: string;
  time: number;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  booking: IBooking;
  onExtended: () => void;
  timeZone?: string;
}

interface IExtensionResult {
  id: string;
  referenceNumber: string;
  status: string;
  endDate: string;
  conflictingBookingReference?: string;
  vehicleAvailableUntil?: string;
}

// NU-12537: self-service extension request. Mirrors the shape of
// GenerateSwapRequestDialog.tsx (also a customer request against their own
// active booking) but is deliberately much simpler - an extension only ever
// needs a new end date, no vehicle re-allocation UI.
export const ExtendBookingDialog: React.FC<IProps> = ({ open, onClose, booking, onExtended, timeZone }) => {
  const { t } = useTranslation();
  const snackbar = useSnackBar();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const currentEndDate = booking.dropoffDateTime || "";
  const bookingBranch = website.branches?.find((branch) => branch.id === booking.branchId);
  const maxExtensionEndDate =
    booking.pickupDateTime &&
    bookingBranch?.maxBookingDurationOptIn &&
    bookingBranch.maxBookingDuration
      ? d.fromISO(booking.pickupDateTime).plus({ days: bookingBranch.maxBookingDuration }).toUTC().toISO()
      : undefined;
  const [newEndDate, setNewEndDate] = useState<string>(currentEndDate);
  const [result, setResult] = useState<IExtensionResult | null>(null);

  const [requestExtension, { loading }] = useMutation(CONSUMER_REQUEST_BOOKING_EXTENSION, {
    onCompleted: (data) => {
      setResult(data.consumerRequestBookingExtension);
      if (data.consumerRequestBookingExtension?.status === "CONFIRMED") {
        onExtended();
      }
    },
    onError: (error) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });
    },
  });

  const handleClose = () => {
    setResult(null);
    setNewEndDate(currentEndDate);
    onClose();
  };

  const handleSubmit = () => {
    requestExtension({
      variables: {
        bookingId: booking.id,
        endDate: newEndDate,
        customerId: user?.businesscustomers ? undefined : user?.customerId,
        businessCustomerId: user?.businesscustomers?.id
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("root:extendBooking")}</DialogTitle>
      <DialogContent>
        {!result && (
          <>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              {t("root:extendBookingDescription")}
            </Typography>
            <SimpleDateTimePicker
              date={newEndDate}
              dateTitle={t("root:newReturnDate")}
              timeTitle={t("root:newReturnTime")}
              name="extensionEndDate"
              minDate={currentEndDate}
              maxDate={maxExtensionEndDate}
              timeZone={timeZone}
              handleChange={(date: IDateTime) => setNewEndDate(date.date)}
            />
          </>
        )}
        {result && result.status === "CONFIRMED" && (
          <Typography variant="body1">
            {t("root:extensionConfirmedMessage", {
              date: d.fromISO(result.endDate).toFormat("dd LLL yyyy, hh:mm a"),
            })}
          </Typography>
        )}
        {result && result.status === "QUOTE" && (
          <Typography variant="body1">
            {result.vehicleAvailableUntil
              ? t("root:extensionConflictMessageWithBoundary", {
                  date: d.fromISO(result.vehicleAvailableUntil).toFormat("dd LLL yyyy, hh:mm a"),
                })
              : t("root:extensionConflictMessage")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {!result && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              {t("misc:cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading || !newEndDate || newEndDate <= currentEndDate}
            >
              {loading ? <CircularProgress size={20} /> : t("root:requestExtension")}
            </Button>
          </>
        )}
        {result && (
          <Button onClick={handleClose} color="primary" variant="contained">
            {t("misc:close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExtendBookingDialog;
